/**
 * Shared utilities for seat-map components
 * Copied from @liveflight/utilities/seat-map and @liveflight/resources/seat-map
 * Components should import from './logic' instead of utilities
 */

import { is_child, is_infant } from '@liveflight/utilities/mixins';

// ============================================
// CONSTANTS (from resources/seat-map.js)
// ============================================

export const SEAT_NOT_SUITABLE_FOR_INFANT = '1A';
export const SEAT_SUITABLE_FOR_INFANT = 'I';
export const SEAT_NOT_SUITABLE_FOR_CHILD = 'IE';
export const SEAT_SUITABLE_FOR_CHILD = 'U';

// Premium seat characteristics (from resources/seat-map.js)
const nonStandardSeatCharacteristics = {
  MV: 'Row with movie screen',
  '1A_AQC_PREMIUM_SEAT': 'Premium seat',
};

export const premiumSeatCharacteristics = {
  ...nonStandardSeatCharacteristics,
};

export const userKnowledgeSeatCharacteristics = {
  ...premiumSeatCharacteristics,
  [SEAT_SUITABLE_FOR_INFANT]: 'Seat suitable for adult with an infant',
  '1M': 'Seat with movie view',
  [SEAT_SUITABLE_FOR_CHILD]: 'Seat suitable for unaccompanied minors',
};

// ============================================
// PASSENGER UTILITIES (from utilities/seat-map.js)
// ============================================

export const filterHeldPassengers = (passengers) =>
  passengers.filter((p) => {
    if (p.travelerType) return p.travelerType !== 'HELD_INFANT';
    return p.traveler_type !== 'HELD_INFANT';
  });

const travelerTypePriority = (passenger) => {
  if (is_infant(passenger) || passenger.holding) return 2;
  if (is_child(passenger)) return 1;
  return 0;
};

export const sortPassengers = (passengers) =>
  passengers.sort((p1, p2) => travelerTypePriority(p2) - travelerTypePriority(p1));

// ============================================
// SEAT AVAILABILITY (from utilities/seat-map.js)
// ============================================

export const isSeatAvailableForAnyPassenger = (seat) =>
  seat.travelerPricing.some(
    (pricing) => pricing.seatAvailabilityStatus.toUpperCase() === 'AVAILABLE'
  );

export const isSeatAvailableForAllPassengers = (seat) =>
  seat.travelerPricing.every(
    (pricing) => pricing.seatAvailabilityStatus.toUpperCase() === 'AVAILABLE'
  );

export const isSeatAvailableForPassenger = (travelerId, seat) => {
  if (!seat) {
    return false;
  }
  const travelerPricing = (seat.travelerPricing || []).reduce(
    (pricing, current) => (current.travelerId === travelerId ? current : pricing),
    undefined
  );
  return (
    !!travelerPricing &&
    String(travelerPricing.seatAvailabilityStatus).toUpperCase() === 'AVAILABLE'
  );
};

// ============================================
// SEAT PRICING (from utilities/seat-map.js)
// ============================================

export const getTravelerPricing = (seat, travelerId, fallbackToDefault = true) => {
  if (!seat) {
    return {};
  }
  const { travelerPricing = [] } = seat;
  const pricingIndex = travelerPricing.findIndex((pricing) => pricing.travelerId === travelerId);
  if (!fallbackToDefault) {
    return pricingIndex >= 0 ? travelerPricing[pricingIndex] : {};
  }
  if (travelerPricing && travelerPricing.length) {
    return travelerPricing[Math.max(0, pricingIndex)];
  }
  return {};
};

export const getSeatPrice = (seat, travelerId, markedUp = true) => {
  const { price: seatPrice } = getTravelerPricing(seat, travelerId, false);
  if (!seatPrice) {
    return 0;
  }

  let grandTotal = seatPrice.total || 0;
  if (markedUp) {
    grandTotal = seatPrice.grandTotal || seatPrice.total;
  }
  // Keep it to cents decimal
  return Math.ceil(Number(grandTotal) * 100) / 100;
};

// ============================================
// SEAT RETRIEVAL (from utilities/seat-map.js)
// ============================================

export const getSeatsForTraveler = (flightOffer, travelerId) => {
  const seats = [];
  try {
    const pricings = flightOffer.travelerPricings.filter((t) => t.travelerId === travelerId);
    for (let i = 0; i < pricings.length; i += 1) {
      const fareDetailsBySegment = pricings[i].fareDetailsBySegment;
      if (fareDetailsBySegment) {
        for (let j = 0; j < fareDetailsBySegment.length; j += 1) {
          const fare = fareDetailsBySegment[j];
          if (fare.additionalServices && fare.additionalServices.chargeableSeatNumber) {
            seats.push({
              segmentId: fare.segmentId,
              number: fare.additionalServices.chargeableSeatNumber,
            });
          }
        }
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('getSeatsForTraveler error', e);
  }
  return seats;
};

export const getSeatsForSegment = (flightOffer, segmentId) => {
  const seats = [];
  try {
    const pricings = flightOffer.travelerPricings;
    for (let i = 0; i < pricings.length; i += 1) {
      const travelerId = pricings[i].travelerId;
      const fareDetailsBySegment = pricings[i].fareDetailsBySegment.filter(
        (fare) => fare.segmentId === segmentId
      );
      if (fareDetailsBySegment) {
        for (let j = 0; j < fareDetailsBySegment.length; j += 1) {
          const fare = fareDetailsBySegment[j];
          if (fare.additionalServices && fare.additionalServices.chargeableSeatNumber) {
            seats.push({
              travelerId,
              number: fare.additionalServices.chargeableSeatNumber,
            });
          }
        }
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('getSeatsForSegment error', e);
  }
  return seats;
};

export const getSegmentsFromFlightOffers = (flightOffers) => {
  const segmentsMap = {};
  const allSegments = flightOffers.flatMap((offer) =>
    offer.itineraries.flatMap((itinerary) => itinerary.segments)
  );
  for (let index = 0; index < allSegments.length; index++) {
    const segment = allSegments[index];
    segmentsMap[segment.id] = segment;
  }
  return segmentsMap;
};

// ============================================
// DISPLAY HELPERS (from utilities/seat-map.js)
// ============================================

// Note: This requires displayedAvailability from resources
// For now, inline the display logic
export const getDisplayAvailability = (availability) => {
  const displayMap = {
    available: 'Disponibile',
    occupied: 'Occupato',
    blocked: 'Bloccato',
  };
  const key = (availability || '').toLowerCase();
  return displayMap[key] || availability;
};

// From utilities/mixins.js
export function formatPassengerName(passenger, fullName = false) {
  if (!passenger) return '';
  return [
    passenger.firstname,
    fullName ? passenger.middlename : undefined,
    passenger.lastname,
    fullName ? passenger.second_lastname : undefined,
  ]
    .filter((e) => !!e)
    .join(' ');
}
